import React, { useState, useEffect, useRef, useContext } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import SendIcon from "@mui/icons-material/Send";
import StorefrontIcon from "@mui/icons-material/Storefront";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

const formatPrice = (price) => {
  if (!price && price !== 0) return "";
  const num = typeof price === "number" ? price : parseFloat(String(price).replace(/[^\d.,]/g, "").replace(",", "."));
  if (isNaN(num)) return String(price);
  return `R$ ${num.toFixed(2).replace(".", ",")}`;
};

const buildProductText = (p) => {
  const lines = [`🛍 *${p.nome}*`];
  if (p.codigo) lines.push(`Código: ${p.codigo}`);
  if (p.preco) lines.push(`Preço: ${formatPrice(p.preco)}`);
  if (p.descricaoCurta) lines.push(p.descricaoCurta.slice(0, 120));
  if (p.estoque != null && typeof p.estoque !== "object") lines.push(`Estoque: ${p.estoque} un.`);
  return lines.join("\n");
};

const ProductCatalogDialog = ({ open, onClose, whatsappId, contactNumber, ticketId, onProductSelect }) => {
  const { user } = useContext(AuthContext);
  const [tab, setTab] = useState(0);
  const [blingEnabled, setBlingEnabled] = useState(false);

  // WA catalog state
  const [waProducts, setWaProducts] = useState([]);
  const [waLoading, setWaLoading] = useState(false);

  // Bling state
  const [blingSearch, setBlingSearch] = useState("");
  const [blingProducts, setBlingProducts] = useState([]);
  const [blingLoading, setBlingLoading] = useState(false);
  const [sending, setSending] = useState(null);

  // Category state
  const [categories, setCategories] = useState([]);
  const [selectedCatId, setSelectedCatId] = useState(null);
  const [selectedSubCatId, setSelectedSubCatId] = useState(null);

  const searchTimer = useRef(null);

  // Check Bling status on open (só admin tem acesso ao endpoint)
  useEffect(() => {
    if (!open || user?.profile !== "admin") return;
    api.get("/bling/status").then(({ data }) => setBlingEnabled(data.connected)).catch(() => {});
  }, [open, user?.profile]);

  // Load categories when Bling is enabled
  useEffect(() => {
    if (!open || !blingEnabled) return;
    if (categories.length > 0) return;
    api.get("/bling/categories")
      .then(({ data }) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [open, blingEnabled]);

  // Load WA catalog
  useEffect(() => {
    if (!open || tab !== 1 || !whatsappId || !contactNumber) return;
    setWaProducts([]);
    setWaLoading(true);
    const businessId = `${contactNumber.replace(/\D/g, "")}@c.us`;
    api.get(`/whatsapp/${whatsappId}/products`, { params: { businessId, qnt: 20 }, timeout: 10000 })
      .then(({ data }) => setWaProducts(Array.isArray(data) ? data : []))
      .catch(() => setWaProducts([]))
      .finally(() => setWaLoading(false));
  }, [open, tab, whatsappId, contactNumber]);

  // Load Bling products (debounced on search or category change)
  const activeCatId = selectedSubCatId ?? selectedCatId ?? undefined;
  useEffect(() => {
    if (!open || tab !== 0 || !blingEnabled) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setBlingLoading(true);
      const params = { search: blingSearch, limit: 20 };
      if (activeCatId) params.idCategoria = activeCatId;
      api.get("/bling/products", { params })
        .then(({ data }) => setBlingProducts(Array.isArray(data) ? data : []))
        .catch(() => setBlingProducts([]))
        .finally(() => setBlingLoading(false));
    }, blingSearch ? 500 : 0);
    return () => clearTimeout(searchTimer.current);
  }, [open, tab, blingEnabled, blingSearch, activeCatId]);

  const rootCategories = categories.filter(c => c.categoriaPaiId === 0);
  const subCategories = selectedCatId
    ? categories.filter(c => c.categoriaPaiId === selectedCatId)
    : [];

  const handleSelectRootCat = (id) => {
    if (selectedCatId === id) {
      setSelectedCatId(null);
      setSelectedSubCatId(null);
    } else {
      setSelectedCatId(id);
      setSelectedSubCatId(null);
    }
  };

  const handleSelectSubCat = (id) => {
    setSelectedSubCatId(prev => (prev === id ? null : id));
  };

  const proxyImageUrl = (url) =>
    url?.startsWith("https://orgbling.s3.amazonaws.com/")
      ? `/bling/proxy-image?url=${encodeURIComponent(url)}`
      : url || undefined;

  const handleBlingProduct = async (product) => {
    const text = buildProductText(product);
    if (onProductSelect) {
      setSending(product.id);
      let imageFile = null;
      try {
        // Fetch full-quality image URL from product detail endpoint
        let fullImageUrl = product.imagemURL || null;
        try {
          const { data } = await api.get(`/bling/products/${product.id}/image`);
          if (data.imageUrl) fullImageUrl = data.imageUrl;
        } catch {
          // fallback to thumbnail URL from list
        }

        if (fullImageUrl) {
          const proxyUrl = proxyImageUrl(fullImageUrl);
          const res = await fetch(proxyUrl);
          if (res.ok) {
            const blob = await res.blob();
            imageFile = new File([blob], "produto.jpg", { type: "image/jpeg" });
          }
        }
      } catch {
        // download failed — send text only
      } finally {
        setSending(null);
      }
      onProductSelect(text, imageFile);
      onClose();
    } else {
      // Fallback: send directly via API (ContactDrawer usage without onProductSelect)
      if (!ticketId) return;
      setSending(product.id);
      try {
        await api.post(`/bling/ticket/${ticketId}/send-product/${product.id}`);
        toast.success(`${product.nome} enviado`);
        onClose();
      } catch (err) {
        toastError(err);
      } finally {
        setSending(null);
      }
    }
  };

  const handleSendWA = async (product) => {
    const name = product.name || product.title || "Produto";
    const price = formatPrice(product.price || product.priceInfo?.price);
    const desc = product.description ? ` — ${product.description.slice(0, 80)}` : "";
    const text = `*${name}*${price ? `\nPreço: ${price}` : ""}${desc}`;
    if (onProductSelect) {
      onProductSelect(text, null);
      onClose();
    } else {
      setSending(product.id || name);
      try {
        await api.post(`/messages/${ticketId}`, { body: text, fromMe: true });
        toast.success(`${name} enviado`);
        onClose();
      } catch (err) {
        toastError(err);
      } finally {
        setSending(null);
      }
    }
  };

  const showTabs = blingEnabled;

  const chipStyle = (active) => ({
    cursor: "pointer",
    flexShrink: 0,
    backgroundColor: active ? "#F37021" : undefined,
    color: active ? "#fff" : undefined,
    borderColor: active ? "#F37021" : undefined,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle style={{ paddingBottom: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <StorefrontIcon style={{ verticalAlign: "middle" }} />
          Catálogo de produtos
        </div>
      </DialogTitle>

      {showTabs && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          style={{ borderBottom: "1px solid #e0e0e0", minHeight: 40 }}
          TabIndicatorProps={{ style: { backgroundColor: "#F37021" } }}
        >
          <Tab label="Bling ERP" style={{ minHeight: 40, fontSize: 13, textTransform: "none", color: tab === 0 ? "#F37021" : undefined }} />
          <Tab label="Catálogo WA" style={{ minHeight: 40, fontSize: 13, textTransform: "none", color: tab === 1 ? "#F37021" : undefined }} />
        </Tabs>
      )}

      <DialogContent style={{ minHeight: 200, paddingTop: 12 }}>
        {/* Bling tab */}
        {(tab === 0 || !showTabs) && blingEnabled && (
          <>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Buscar produto..."
              value={blingSearch}
              onChange={e => setBlingSearch(e.target.value)}
              autoFocus
              style={{ marginBottom: 8 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" style={{ color: "#8A9096" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Category chips */}
            {rootCategories.length > 0 && (
              <div style={{ overflowX: "auto", display: "flex", gap: 6, paddingBottom: 6, flexWrap: "nowrap" }}>
                <Chip
                  label="Todas"
                  size="small"
                  variant={selectedCatId === null ? "filled" : "outlined"}
                  style={chipStyle(selectedCatId === null)}
                  onClick={() => { setSelectedCatId(null); setSelectedSubCatId(null); }}
                />
                {rootCategories.map(cat => (
                  <Chip
                    key={cat.id}
                    label={cat.descricao}
                    size="small"
                    variant={selectedCatId === cat.id ? "filled" : "outlined"}
                    style={chipStyle(selectedCatId === cat.id)}
                    onClick={() => handleSelectRootCat(cat.id)}
                  />
                ))}
              </div>
            )}

            {/* Subcategory chips */}
            {subCategories.length > 0 && (
              <div style={{ overflowX: "auto", display: "flex", gap: 6, paddingBottom: 8, flexWrap: "nowrap" }}>
                {subCategories.map(cat => (
                  <Chip
                    key={cat.id}
                    label={cat.descricao}
                    size="small"
                    variant={selectedSubCatId === cat.id ? "filled" : "outlined"}
                    style={chipStyle(selectedSubCatId === cat.id)}
                    onClick={() => handleSelectSubCat(cat.id)}
                  />
                ))}
              </div>
            )}

            {blingLoading && (
              <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                <CircularProgress size={28} style={{ color: "#F37021" }} />
              </div>
            )}
            {!blingLoading && blingProducts.length === 0 && (
              <Typography variant="body2" style={{ color: "#8A9096", textAlign: "center", padding: 24 }}>
                {blingSearch || activeCatId ? "Nenhum produto encontrado." : "Selecione uma categoria ou busque por nome."}
              </Typography>
            )}
            {!blingLoading && blingProducts.length > 0 && (
              <List dense disablePadding>
                {blingProducts.map((p) => (
                  <ListItem
                    key={p.id}
                    divider
                    secondaryAction={
                      <Tooltip title="Adicionar à mensagem">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleBlingProduct(p)}
                            disabled={sending === p.id}
                            style={{ color: "#F37021" }}
                          >
                            {sending === p.id ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={p.imagemURL || undefined} variant="rounded" style={{ width: 44, height: 44, background: "#f5f5f5" }}>
                        <StorefrontIcon style={{ color: "#bdbdbd" }} />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={p.nome}
                      secondary={
                        <span>
                          {p.codigo && <span style={{ color: "#8A9096", marginRight: 8 }}>#{p.codigo}</span>}
                          {p.preco ? <span style={{ color: "#273A74", fontWeight: 600 }}>{formatPrice(p.preco)}</span> : null}
                          {p.estoque != null && typeof p.estoque !== "object" && (
                            <span style={{ color: "#8A9096", marginLeft: 8 }}>· est: {p.estoque}</span>
                          )}
                        </span>
                      }
                      primaryTypographyProps={{ style: { fontWeight: 600, fontSize: 13 } }}
                      secondaryTypographyProps={{ component: "div", style: { fontSize: 12 } }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        {/* WA catalog tab */}
        {tab === 1 && showTabs && (
          <>
            {waLoading && (
              <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                <CircularProgress size={28} style={{ color: "#F37021" }} />
              </div>
            )}
            {!waLoading && waProducts.length === 0 && (
              <Typography variant="body2" style={{ color: "#8A9096", textAlign: "center", padding: 32 }}>
                Nenhum produto encontrado para este contato.
              </Typography>
            )}
            {!waLoading && waProducts.length > 0 && (
              <List dense>
                {waProducts.map((p) => {
                  const name = p.name || p.title || "Sem nome";
                  const price = formatPrice(p.price || p.priceInfo?.price);
                  const thumb = p.imageUrl || p.image || null;
                  return (
                    <ListItem
                      key={p.id || name}
                      divider
                      secondaryAction={
                        <Tooltip title="Adicionar à mensagem">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleSendWA(p)}
                              disabled={sending === (p.id || name)}
                              style={{ color: "#F37021" }}
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar src={thumb} variant="rounded" style={{ width: 44, height: 44, background: "#f5f5f5" }}>
                          <StorefrontIcon style={{ color: "#bdbdbd" }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={name}
                        secondary={price || p.description?.slice(0, 60)}
                        primaryTypographyProps={{ style: { fontWeight: 600, fontSize: 13 } }}
                        secondaryTypographyProps={{ style: { fontSize: 12 } }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}

        {/* Sem Bling — catálogo WA direto */}
        {!blingEnabled && (
          <>
            {waLoading && (
              <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
                <CircularProgress size={28} />
              </div>
            )}
            {!waLoading && waProducts.length === 0 && (
              <Typography variant="body2" style={{ color: "#8A9096", textAlign: "center", padding: 32 }}>
                Nenhum produto encontrado para este contato.
              </Typography>
            )}
            {!waLoading && waProducts.length > 0 && (
              <List dense>
                {waProducts.map((p) => {
                  const name = p.name || p.title || "Sem nome";
                  const price = formatPrice(p.price || p.priceInfo?.price);
                  const thumb = p.imageUrl || p.image || null;
                  return (
                    <ListItem
                      key={p.id || name}
                      divider
                      secondaryAction={
                        <Tooltip title="Adicionar à mensagem">
                          <span>
                            <IconButton size="small" onClick={() => handleSendWA(p)} disabled={sending === (p.id || name)} style={{ color: "#F37021" }}>
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar src={thumb} variant="rounded" style={{ width: 44, height: 44, background: "#f5f5f5" }}>
                          <StorefrontIcon style={{ color: "#bdbdbd" }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={name}
                        secondary={price || p.description?.slice(0, 60)}
                        primaryTypographyProps={{ style: { fontWeight: 600, fontSize: 13 } }}
                        secondaryTypographyProps={{ style: { fontSize: 12 } }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ProductCatalogDialog;
